// Merchant Center Account Issues — Feed Health section, deterministic check (Merchant API).
// Account-level health: suspensions / policy / account-wide misconfig, by severity.

import { count } from '../../utils/format';
import type { EvidenceProps } from './registry';

interface IssueRow {
  title: string;
  severity: string; // CRITICAL | ERROR | SUGGESTION | UNKNOWN
  detail: string | null;
  documentationUri: string | null;
}

interface AccountIssuesShape {
  merchantId: string;
  issueCount: number;
  bySeverity: { critical: number; error: number; suggestion: number };
  issues: IssueRow[];
}

const SEVERITY_LABEL: Record<string, string> = {
  CRITICAL: 'Critical',
  ERROR: 'Error',
  SUGGESTION: 'Suggestion'
};

export const AccountIssuesEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as AccountIssuesShape;

  if (e.issueCount === 0) {
    return (
      <div className='evidence'>
        <p className='evidence__footnote'>
          No account-level issues on Merchant Center {e.merchantId} — no suspensions, policy
          problems, or account-wide misconfigurations are flagged.
        </p>
      </div>
    );
  }

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Account issues
          <span className='evidence__section-tag evidence__section-tag--context'>
            Merchant Center {e.merchantId}
          </span>
        </h4>
        <div className='audience-coverage'>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{count(e.bySeverity.critical)}</div>
            <div className='audience-coverage__lbl'>Critical</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{count(e.bySeverity.error)}</div>
            <div className='audience-coverage__lbl'>Error</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{count(e.bySeverity.suggestion)}</div>
            <div className='audience-coverage__lbl'>Suggestion</div>
          </div>
        </div>
      </section>

      <section className='evidence__section'>
        <h4 className='evidence__section-title'>Issues</h4>
        <table className='evidence__table'>
          <thead>
            <tr>
              <th>Issue</th>
              <th>Severity</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {e.issues.map((i, idx) => (
              <tr key={`${i.title}-${idx}`}>
                <td>
                  {i.documentationUri ? (
                    <a href={i.documentationUri} target='_blank' rel='noreferrer'>
                      {i.title}
                    </a>
                  ) : (
                    i.title
                  )}
                </td>
                <td>
                  <span
                    className={`evidence__strength ${
                      i.severity === 'CRITICAL' || i.severity === 'ERROR'
                        ? 'evidence__strength--average'
                        : 'evidence__strength--good'
                    }`}
                  >
                    {SEVERITY_LABEL[i.severity] ?? i.severity}
                  </span>
                </td>
                <td>{i.detail ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <p className='evidence__footnote'>
        Account-level Merchant Center issues affect the whole account (vs per-product feed
        problems). Critical issues (e.g. account suspension) can stop all Shopping serving.
      </p>
    </div>
  );
};
